import { useContext, useEffect, useMemo, useState } from "react";
import { formatInTimeZone } from "date-fns-tz";
import {
  Button,
  EnhancedTable,
  useClientTable,
} from "@pushpress/shared-components";
import { AppContext } from "../../../../context/app";
import { CalendarItemAttendeeFragment } from "./../CalendarDetail-generated.hooks";
import AttendeeBadgeColumn from "./AttendeeBadgeColumn";
import AttendeeAvatarNameColumn from "./AttendeeAvatarNameColumn";
import AttendanceMenu from "../AttendanceMenu";
import { chipColor, statusLabelMap } from "../../Calendar.utils";
import CheckinButton from "./CheckinButton";
import AddMemberToEventModal from "../../AddMemberToEventModal";

type AttendanceTableProps = {
  attendees?: CalendarItemAttendeeFragment[];
  onActionSuccess?: VoidFunction;
  calendarItemUuid: string;
};

function getTooltipDate(row: CalendarItemAttendeeFragment, timezone?: string) {
  if (
    (!row.registrationTimestamp ||
      isNaN(new Date(row.registrationTimestamp).getTime())) &&
    (!row.checkinTimestamp || isNaN(new Date(row.checkinTimestamp).getTime()))
  ) {
    return "- -";
  }

  // If it's checkinTimestamp, it's already in UTC
  if (row.checkinTimestamp) {
    return formatInTimeZone(
      row.checkinTimestamp * 1000,
      "UTC",
      "MMM d, h:mmaaa",
    );
  }

  // If not, convert to gyms timezone
  return formatInTimeZone(
    new Date(row.registrationTimestamp * 1000),
    timezone,
    "MMM d, h:mmaaa",
  );
}

const AttendanceTable = ({
  attendees = [],
  onActionSuccess,
  calendarItemUuid,
}: AttendanceTableProps) => {
  const app = useContext(AppContext);
  const [eventModal, setEventModal] = useState({ userUuid: "", show: false });

  const { client } = app;

  const [rowsLoading, setRowsLoading] = useState<
    CalendarItemAttendeeFragment[]
  >([]);

  const renderMenu = (
    row: CalendarItemAttendeeFragment,
    onClose: VoidFunction,
  ) => {
    return (
      <AttendanceMenu
        row={row}
        hideActions={["checkIn"]}
        onClose={onClose}
        onSuccess={onActionSuccess}
        onRowsLoading={setRowsLoading}
      />
    );
  };

  const renderBulkMenu = (
    rows: { [key: string]: CalendarItemAttendeeFragment },
    onClose: VoidFunction,
    onClearSelection: VoidFunction,
  ) => {
    return (
      <AttendanceMenu
        rows={rows}
        onClose={onClose}
        onSuccess={onActionSuccess}
        onRowsLoading={setRowsLoading}
        onClearSelection={onClearSelection}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        key: "customerName",
        header: "Name",
        isSortable: true,
        sort: (
          a: CalendarItemAttendeeFragment,
          b: CalendarItemAttendeeFragment,
        ) => a?.customerName?.localeCompare(b?.customerName || "") || 0,
        cell: (row: CalendarItemAttendeeFragment) => {
          return <AttendeeAvatarNameColumn attendee={row} />;
        },
      },
      {
        header: "Plan",
        key: "plan",
        isSortable: true,
        hideOnMobile: true,
        cell: (row: CalendarItemAttendeeFragment) => {
          return row?.plan ?? "--";
        },
      },
      {
        header: "Status",
        key: "status",
        isSortable: true,
        hideOnMobile: true,
        cell: (row: CalendarItemAttendeeFragment) => {
          const loading = rowsLoading.some((r) => r.uuid === row.uuid);
          return (
            <AttendeeBadgeColumn
              label={statusLabelMap[row.status]}
              color={chipColor[row.status]}
              loading={loading}
              tooltip={getTooltipDate(row, client.timezone)}
            />
          );
        },
      },
      {
        header: "",
        key: "actions",
        width: "120px",
        hideOnMobile: true,
        cell: (row: CalendarItemAttendeeFragment) => {
          if (row.status === "invited") {
            return (
              <Button
                text="Register"
                buttonType="secondary"
                onClick={() =>
                  setEventModal({ userUuid: row.userUuid, show: true })
                }
              />
            );
          }
          return (
            <CheckinButton
              registrationUuid={row.uuid}
              hide={row.status === "checkedin"}
              onSuccess={onActionSuccess}
            />
          );
        },
      },
    ],
    [rowsLoading, client?.timezone, onActionSuccess],
  );

  const {
    data: tableData,
    sort,
    handleSort,
    handlePageChange,
    getRowId,
  } = useClientTable({
    data: attendees.map((r) => r),
    columns,
    defaultSort: { key: "status", order: "asc" },
    keepOriginalSorting: true,
    getRowId: (row) => row.uuid,
  });

  // After actions success the onActionSuccess is called
  // and a refetch will be fired that will update the attendees and clean the loading
  useEffect(() => {
    setRowsLoading([]);
  }, [attendees]);

  return (
    <>
      <AddMemberToEventModal
        visible={eventModal.show}
        onClose={() => {
          setEventModal({ userUuid: "", show: false });
        }}
        onSuccess={() => {
          onActionSuccess();
          setEventModal({ userUuid: "", show: false });
        }}
        calendarUuid={calendarItemUuid}
        userUuid={eventModal.userUuid}
      />

      <EnhancedTable
        data={tableData}
        columns={columns}
        sort={sort}
        selectable
        getRowId={getRowId!}
        renderRowMenu={renderMenu}
        renderBulkMenu={renderBulkMenu}
        onSort={handleSort}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default AttendanceTable;
